/**
 * VoiceRecorder.js - Voice recording component for voicemails
 * Handles mic permissions, recording, playback, and upload
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic, MicOff, Square, Play, Pause, Trash2, Send, X,
  Clock, AlertCircle, CheckCircle, Volume2
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

const MAX_DURATION = 120; // 2 minutes max

const VoiceRecorder = ({
  recipientId,
  recipientName,
  onClose,
  onSuccess
}) => {
  // Recording state
  const [permissionStatus, setPermissionStatus] = useState('pending'); // pending, granted, denied
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [visualizerData, setVisualizerData] = useState(new Array(32).fill(0));

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const isRecordingRef = useRef(false); // Use ref for animation loop to avoid stale closure

  // Check microphone permission
  const checkPermission = useCallback(async () => {
    try {
      // Try to query permission status
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setPermissionStatus(result.state);
        
        result.onchange = () => {
          setPermissionStatus(result.state);
        };
        
        if (result.state === 'granted') {
          return true;
        }
      }
      
      // Request permission by getting user media
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');
      return true;
    } catch (error) {
      console.error('Permission check error:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionStatus('denied');
      }
      return false;
    }
  }, []);

  // Request microphone permission
  const requestPermission = useCallback(async () => {
    try {
      setPermissionStatus('pending');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');
      toast.success('Microphone access granted');
      return true;
    } catch (error) {
      console.error('Permission request error:', error);
      setPermissionStatus('denied');
      
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone.');
      } else {
        toast.error(`Microphone error: ${error.message}`);
      }
      return false;
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Set up audio analyser for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Start visualization - use ref to avoid stale closure
      isRecordingRef.current = true;
      const updateVisualizer = () => {
        if (analyserRef.current && isRecordingRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);
          setVisualizerData(Array.from(dataArray).slice(0, 32));
          animationRef.current = requestAnimationFrame(updateVisualizer);
        }
      };
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Clean up
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
      // Start visualizer
      updateVisualizer();
      
    } catch (error) {
      console.error('Start recording error:', error);
      toast.error('Failed to start recording');
    }
  }, [isRecording]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      isRecordingRef.current = false; // Stop visualizer animation
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      setVisualizerData(new Array(32).fill(0));
    }
  }, [isRecording]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    isRecordingRef.current = false; // Stop visualizer animation
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsRecording(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingDuration(0);
    setVisualizerData(new Array(32).fill(0));
    audioChunksRef.current = [];
  }, [isRecording]);

  // Delete recorded audio
  const deleteRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingDuration(0);
    audioChunksRef.current = [];
  }, [audioUrl]);

  // Play/pause recorded audio
  const togglePlayback = useCallback(() => {
    if (!audioRef.current || !audioUrl) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, audioUrl]);

  // Send voicemail
  const sendVoicemail = useCallback(async () => {
    if (!audioBlob || !recipientId) return;
    
    setIsSending(true);
    
    try {
      const formData = new FormData();
      const extension = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
      formData.append('file', audioBlob, `voicemail.${extension}`);
      formData.append('recipient_id', recipientId);
      formData.append('duration_seconds', recordingDuration.toString());
      
      await api.post('/support/voicemails', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Voicemail sent to ${recipientName || 'Support'}`);
      
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Send voicemail error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send voicemail');
    } finally {
      setIsSending(false);
    }
  }, [audioBlob, recipientId, recipientName, recordingDuration, onSuccess, onClose]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Audio playback handlers
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [audioUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false; // Stop any animation
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[var(--background-paper)] rounded-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-500" />
                Leave Voicemail
              </h3>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                For: {recipientName || 'Support'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--background-elevated)]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Permission denied state */}
          {permissionStatus === 'denied' && (
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
                <MicOff className="w-10 h-10 text-red-500" />
              </div>
              <div>
                <h4 className="font-semibold text-lg">Microphone Access Required</h4>
                <p className="text-sm text-[var(--text-muted)] mt-2">
                  Please allow microphone access in your browser settings to record a voicemail.
                </p>
              </div>
              <Button onClick={requestPermission} className="btn-primary">
                Try Again
              </Button>
            </div>
          )}

          {/* Permission pending / granted states */}
          {permissionStatus !== 'denied' && (
            <>
              {/* Visualizer / Recording indicator */}
              <div className="flex flex-col items-center gap-4">
                {/* Recording button with visualizer */}
                <div className="relative">
                  <motion.div
                    className={`w-32 h-32 rounded-full flex items-center justify-center transition-all ${
                      isRecording 
                        ? 'bg-red-500 animate-pulse' 
                        : audioBlob 
                          ? 'bg-green-500/20' 
                          : 'bg-purple-500/20 hover:bg-purple-500/30'
                    }`}
                    whileHover={{ scale: !isRecording && !audioBlob ? 1.05 : 1 }}
                    whileTap={{ scale: !isRecording && !audioBlob ? 0.95 : 1 }}
                  >
                    {isRecording ? (
                      <div className="text-center text-white">
                        <Clock className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-2xl font-bold">{formatTime(recordingDuration)}</span>
                        <p className="text-xs mt-1">/ {formatTime(MAX_DURATION)}</p>
                      </div>
                    ) : audioBlob ? (
                      <CheckCircle className="w-12 h-12 text-green-500" />
                    ) : (
                      <Mic className="w-12 h-12 text-purple-500" />
                    )}
                  </motion.div>

                  {/* Audio visualizer bars */}
                  {isRecording && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="flex items-end gap-0.5 h-16">
                        {visualizerData.slice(0, 16).map((value, i) => (
                          <motion.div
                            key={`visualizer-bar-${i}`}
                            className="w-1 bg-white/50 rounded-full"
                            animate={{ height: Math.max(4, value / 4) }}
                            transition={{ duration: 0.1 }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status text */}
                <p className="text-center text-[var(--text-muted)]">
                  {isRecording 
                    ? 'Recording... Click stop when done' 
                    : audioBlob 
                      ? `${formatTime(recordingDuration)} recorded`
                      : 'Click to start recording'}
                </p>
              </div>

              {/* Audio playback (if recorded) */}
              {audioUrl && !isRecording && (
                <div className="bg-[var(--background-elevated)] rounded-xl p-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={togglePlayback}
                      className="w-12 h-12 rounded-full bg-purple-500 text-white flex items-center justify-center hover:bg-purple-600 transition-colors"
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </button>
                    <div className="flex-1">
                      <div className="h-2 bg-[var(--background-paper)] rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: '100%' }} />
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {formatTime(recordingDuration)}
                      </p>
                    </div>
                    <button
                      onClick={deleteRecording}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-500"
                      title="Delete recording"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <audio ref={audioRef} src={audioUrl} className="hidden" />
                </div>
              )}

              {/* Controls */}
              <div className="flex gap-3">
                {!isRecording && !audioBlob && (
                  <Button
                    onClick={startRecording}
                    className="flex-1 bg-purple-500 hover:bg-purple-600"
                    disabled={permissionStatus === 'pending'}
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Start Recording
                  </Button>
                )}

                {isRecording && (
                  <>
                    <Button
                      onClick={cancelRecording}
                      variant="outline"
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={stopRecording}
                      className="flex-1 bg-red-500 hover:bg-red-600"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Stop
                    </Button>
                  </>
                )}

                {audioBlob && !isRecording && (
                  <>
                    <Button
                      onClick={deleteRecording}
                      variant="outline"
                      className="flex-1"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Re-record
                    </Button>
                    <Button
                      onClick={sendVoicemail}
                      disabled={isSending}
                      className="flex-1 bg-green-500 hover:bg-green-600"
                    >
                      {isSending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>

              {/* Tips */}
              <div className="text-xs text-center text-[var(--text-muted)] space-y-1">
                <p>Maximum recording time: {formatTime(MAX_DURATION)}</p>
                <p>Tip: Speak clearly and include your callback number</p>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VoiceRecorder;
